import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BehaviorSubject, from } from 'rxjs';
import { ResponseUpdateItem, StepItemConfig } from '@sondermind/utilities/models-flows';
import { PipesModule } from '@sondermind/utilities/pipes';

import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { SegmentService } from '@sondermind/segment';
import { AssessmentProgressStore } from '@sondermind/data-access/assessments';
import { getAssessmentTypeFromSlug } from '@sondermind/utilities/helpers/assessment-helpers';

import { StepComponent } from '../step.component';
import { WizardService } from '../../../services/wizard.service';
import {
  FlowsHostSegmentEvents,
  IClinicalQuestionnaireClickedDoneData,
  IFlowsHostSegmentSchema
} from '../../../models/flows-host-segment-schema.interface';

interface IAssessmentCompleteConfig extends StepItemConfig {
  content?: string;
  helpText: string;
}

@Component({
  selector: 'assessment-complete',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    IrisButtonModule,
  ],
  templateUrl: './assessment-complete.component.html',
  styleUrls: ['./assessment-complete.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AssessmentCompleteComponent extends StepComponent<IAssessmentCompleteConfig> implements OnInit {
  irisButtonTypes = IrisButtonTypes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;
  hasNextAssessment: boolean = false;
  isProviderCompleted: boolean = false;

  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  // If the CQ is completed by provider, overwrite the message from the formwizard,
  // which is directed towards a client
  providerCqCompletionMessage: string =
    `Talk with your client about their results, and make sure to keep an eye on any changes in their responses.`;

  constructor(
    wizard: WizardService,
    private segmentService: SegmentService<IFlowsHostSegmentSchema>,
    private assessmentProgressStore: AssessmentProgressStore,
  ) {
    super(wizard);
  }

  get hasContext(): boolean {
    return !!this.wizard.context;
  }

  get staticIntroContent(): string {
    const content = this.hasContext ? this.wizard.context : this.config.content;

    return content ?? '';
  }

  get helpText(): string {
    const helpText = this.hasContext ? this.wizard.context : this.config.helpText;

    return helpText ?? '';
  }

  onClickDone(): void {
    // Don't send client-specific CQ-finished event if provider completed the CQ
    if (!this.isProviderCompleted) {
      this.sendClickDoneSegmentEvent();
    }

    this.wizard.complete().subscribe(() => {
      if (this.config.url && !this.isProviderCompleted) {
        const url = this.buildAbsoluteUrl(this.config.url);
        window.location.assign(url);
      } else {
        this.assessmentProgressStore.finishCurrentAssessmentProgress();
      }
    });
  }

  onSubmit(): ResponseUpdateItem[] {
    return [];
  }

  ngOnInit(): void {
    this.valid.next(!!this.component.next);

    this.assessmentProgressStore.markCurrentAssessmentAsCompleted$()
      .subscribe(() => {
        this.hasNextAssessment = this.assessmentProgressStore.hasRemainingAssessments();
        this.isProviderCompleted = !!this.assessmentProgressStore.startedByProviderData;
        if (this.isProviderCompleted) {
          this.sendProviderCompletedSegmentEvent();
        }
        this.loading.next(false);
      });
  }

  onClickNextAssessment(): void {
    this.wizard.toggleRunning(false);
    this.assessmentProgressStore.startNextAssessmentToDo$().subscribe();

    this.sendClickNextSegmentEvent();
  }

  sendClickDoneSegmentEvent(): void {
    const buttonText: string = this.component.done;
    const cqType: string = getAssessmentTypeFromSlug(this.wizard.getSlug());
    const { isPaginated, questionnaireId } = this.wizard;

    const data: IClinicalQuestionnaireClickedDoneData = {
      buttonText,
      cqType,
      isPaginated,
      questionnaireId
    };

    from(
      this.segmentService.track(FlowsHostSegmentEvents.CQ_CLICKED_DONE, data)
    ).subscribe();
  }

  sendClickNextSegmentEvent(): void {
    const cqType: string = getAssessmentTypeFromSlug(this.wizard.getSlug());
    const providerData = this.assessmentProgressStore.startedByProviderData;

    const data = {
      cqType,
      providerId: providerData?.providerId.toString(),
      questionnaireId: this.wizard.questionnaireId
    };

    from(
      this.segmentService.track(FlowsHostSegmentEvents.CQ_CLICKED_NEXT, data)
    ).subscribe();
  }

  private sendProviderCompletedSegmentEvent(): void {
    const flowSlug = this.wizard.getSlug();
    const providerData = this.assessmentProgressStore.startedByProviderData;

    const data = {
      cqType: getAssessmentTypeFromSlug(flowSlug),
      dateAdministered: new Date(),
      location: providerData?.assessmentLaunchedFrom,
      providerId: providerData?.providerId.toString(),
      clientId: providerData?.personaClientId.toString()
    };

    from(
      this.segmentService.track(FlowsHostSegmentEvents.PROVIDER_FINISHES_ASSESSMENT, data)
    ).subscribe();
  }

  private buildAbsoluteUrl(href: string): string {
    const link = document.createElement('a');
    link.href = href;

    return link.href;
  }
}
